import React, { useState } from "react";
import InputField from "components/common/controls/InputField";
import Button from "components/common/controls/Button";
import FormGroup from "components/common/FormGroup";
import Checkbox from "components/common/controls/Checkbox";
import EditorDescriptor from "./EditorDescriptor";
import PromineoPhoneInput, {
  PhoneInputValueChangedEventArg,
} from "components/common/controls/PromineoPhoneInput";
import { useHistory } from "react-router";
import * as routePath from "shared/constants/RoutePathConstants";
import { getTermsAndUsePdfUrl } from "shared/utility/ConfigUtility";

export interface SignUpFormData {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phone: string;
  phoneCode: string;
}

interface Props {
  onSubmit: (arg: SignUpFormData) => void;
}

export default function SignUpForm(props: Props) {
  const history = useHistory();
  const [isTermsAndConditionAgreed, setIsTermsAndConditionAgreed] = useState(false);
  const [state, setState] = useState<SignUpFormData>({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    phoneCode: "",
  });

  const handleSubmit = (evt: React.SyntheticEvent) => {
    evt.preventDefault();
    props.onSubmit(state);
  };

  const handlePhoneNumberChanged = (value: PhoneInputValueChangedEventArg) => {
    setState({
      ...state,
      phone: value.nationalNumber,
      phoneCode: value.countryCode,
    });
  };

  const handleInputValueChange = ({ event }: any) => {
    const targetName = event.target.name;
    const value = event.target.value;
    setState({
      ...state,
      [targetName]: value,
    });
  };

  const handleAlreadyHaveAnAccountClick = () => {
    history.push(routePath.SIGN_IN);
  };

  const checkIfCreateAccountButtonShouldBeDisabled = () => {
    return !isTermsAndConditionAgreed;
  }

  const TermsAgreement = () => {
    return (
      <label className="font-inter text-14px">
        I have read and accept the{" "}
        <a href={getTermsAndUsePdfUrl()} target="_blank" rel="noreferrer" className="underline"> 
           terms and conditions
         </a>
      </label>
    );
  };
  
  return (
    <EditorDescriptor
      title="Sign up"
      description="create an account and
      recieve a free API key"
      descriptionWidthClass="w-191px"
    >
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-x-3 ">
            <FormGroup label="First name">
              <InputField
                placeholder="e.g. John"
                name="firstName"
                maxLength={127}
                required
                id="firstName"
                label="Firstname"
                onChange={handleInputValueChange}
              />
            </FormGroup>
            <FormGroup label="Last name">
              <InputField
                placeholder="e.g. Doe"
                name="lastName"
                maxLength={128}
                required
                id="lastName"
                label="Lastname"
                onChange={handleInputValueChange}
              />
            </FormGroup>
          </div>
          <FormGroup label="Email" tips="Used to verify your login">
            <InputField
              id="signUpEmail"
              mode="email"
              label="Email"
              maxLength={255}
              placeholder="e.g. john.doe@provider.com"
              name="email"
              required={true}
              onChange={handleInputValueChange}
            />
          </FormGroup>
          <FormGroup
            label="Phone (inc. country code)"
            tips="Used to verify your login"
          >
            <PromineoPhoneInput
              onPhoneNumberChanged={handlePhoneNumberChanged}
              value={`${state.phoneCode}${state.phone}`}
              required
            />
          </FormGroup>
          <FormGroup label="Company" tips="optional - to help our support team">
            <InputField
              name="company"
              maxLength={255}
              id="company"
              label="Company"
              placeholder="Company"
              onChange={handleInputValueChange}
            />
          </FormGroup>
        </div>
        <div className="my-6">
              <Checkbox
                labelComponent={<TermsAgreement />}
                name="acceptTerms"
                onValueChange={(e : any) => {
                  setIsTermsAndConditionAgreed(e);
                }}
              />
            </div>
        <div className="mt-8">
          <Button
            className="w-full"
            disabled={checkIfCreateAccountButtonShouldBeDisabled()}
            text="CREATE ACCOUNT"
            useSubmitBehavior={true}
          />
        </div>
        <div className="mt-2">
          <Button
            variant="secondary"
            text="Already have an account?"
            className="w-full"
            onClick={handleAlreadyHaveAnAccountClick}
          />
        </div>
      </form>
    </EditorDescriptor>
  );
}
